import { GraphQL } from "@rpi/openapi-api";

/**
 * Generate all 12 months for a given year as "YYYY-MM" strings
 */
function generateAllMonths(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
}

export function formatYearMonth(month: string, options: Omit<Intl.DateTimeFormatOptions, "timeZone">): string {
  const [year, m] = month.split("-");

  // Date.UTC expects 0-indexed months, but "YYYY-MM" strings use 1-indexed months, so we subtract 1 from the month
  // value. We also set the time zone to UTC to ensure consistent formatting regardless of the user's local time zone.
  return new Date(Date.UTC(Number(year), Number(m) - 1)).toLocaleDateString(undefined, {
    ...options,
    timeZone: "UTC",
  });
}

type OrderDataPoint = NonNullable<GraphQL.OrdersChartQuery["ordersChart"]>["dataPoints"][number];
export type TransformedOrderDataPoint = {
  month: string;
  shipped: number;
  printing: number;
  inProgress: number;
  failed: number;
};

function transformOrderDataPoint(dataPoint: OrderDataPoint): TransformedOrderDataPoint {
  const result: TransformedOrderDataPoint = {
    month: dataPoint.month,
    shipped: 0,
    printing: 0,
    inProgress: 0,
    failed: 0,
  };
  for (const sc of dataPoint.statusCounts) {
    switch (sc.status) {
      case GraphQL.PublicOrderStatus.Shipped:
        result.shipped = sc.count;
        break;
      case GraphQL.PublicOrderStatus.Printing:
        result.printing = sc.count;
        break;
      case GraphQL.PublicOrderStatus.Queued:
      case GraphQL.PublicOrderStatus.Received:
      case GraphQL.PublicOrderStatus.Validating:
      case GraphQL.PublicOrderStatus.ValidAwaitingPayment:
      case GraphQL.PublicOrderStatus.ValidHoldingBin:
        result.inProgress += sc.count;
        break;
      case GraphQL.PublicOrderStatus.Failed:
        result.failed += sc.count;
        break;
    }
  }

  return result;
}

/**
 * Fill in missing months with zero values for order data
 */
export function fillMissingOrderMonths(dataPoints: OrderDataPoint[], year: number): TransformedOrderDataPoint[] {
  const allMonths = generateAllMonths(year);

  return allMonths.map((month) => {
    const dataPoint = dataPoints.find((dp) => dp.month === month);

    if (dataPoint) {
      return transformOrderDataPoint(dataPoint);
    }
    return {
      month,
      shipped: 0,
      printing: 0,
      inProgress: 0,
      failed: 0,
    };
  });
}

type PaymentDataPoint = NonNullable<GraphQL.PaymentsChartQuery["paymentsChart"]>["dataPoints"][number];

export type TransformedPaymentDataPoint = {
  month: string;
  netPaymentAmount: number;
  currency: GraphQL.SupportedCurrency;
};

function transformPaymentDataPoint(dataPoint: PaymentDataPoint): TransformedPaymentDataPoint {
  return {
    month: dataPoint.month,
    netPaymentAmount: dataPoint.netPaymentAmount,
    currency: dataPoint.currency,
  };
}

/**
 * Fill in missing months with zero values for payment data
 */
export function fillMissingPaymentMonths(
  dataPoints: PaymentDataPoint[],
  year: number,
  defaultCurrency: GraphQL.SupportedCurrency = GraphQL.SupportedCurrency.Usd
): TransformedPaymentDataPoint[] {
  const allMonths = generateAllMonths(year);

  // Get currency from first data point if available, otherwise use default
  const currency = dataPoints.length > 0 ? dataPoints[0].currency : defaultCurrency;

  return allMonths.map((month) => {
    const dataPoint = dataPoints.find((dp) => dp.month === month);

    if (dataPoint) {
      return transformPaymentDataPoint(dataPoint);
    }
    return {
      month,
      netPaymentAmount: 0,
      currency,
    };
  });
}
